import { z } from 'zod/v3';
import { GraphModel } from './graph.model';
import { DG } from '@cotera/sdk/core';
import {
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  Watchable,
  ReadonlyWatchable,
} from '../actions/framework';
import { Assert } from '@cotera/utilities';
import {
  ActionResult,
  AskResponse,
  TargetableModel,
} from '../actions/framework/types';
import { useState } from 'react';
import { Loading, ModalPageView } from '@cotera/client/app/components';
import { Button } from '@cotera/client/v0/components/ui/button';
import { err, ok, Result } from 'neverthrow';
import { GRAPH_ACTION_GROUP } from '../dataset/actions/constants';

// Import all the model types we support
import { ExpressionColumnModel } from '../columns/expression/expression-column.model';
import { LlmColumnModel } from '../columns/llm/llm-column.model';
import { TaxonomyColumnModel } from '../columns/taxonomy/taxonomy-column.model';
import { ToolCallModel } from '../columns/tool-call/tool-call.model';
import { DatasetModel } from '../dataset/model/dataset.model';
import { DocumentModel } from '../document/document.model';
import { AgentModel } from '../agent/agent.model';
import { ToolModel } from '../tool/tool.model';
import { Input } from '../components/ui/input';

type DuplicateParams = {
  newSymbol: string;
};

type DuplicatableModel = TargetableModel & {
  currentNameInGraph:
    | Watchable<string | null>
    | Watchable<string>
    | ReadonlyWatchable<string | null>
    | ReadonlyWatchable<string>;
};

type ModelInfo = {
  model: DuplicatableModel;
  symbol: string;
  type: 'column' | 'dataset' | 'document' | 'agent' | 'tool';
  datasetName?: string; // Required for column type
};

function getTargetedModel(
  context: ApplicableContext | ExecuteContext
): ModelInfo | null {
  // Check columns (currentNameInGraph is non-nullable for columns)
  // Check each column type individually to avoid type issues
  const exprColumn = context.targetedModelOfType(ExpressionColumnModel);
  if (exprColumn !== null) {
    const symbol = exprColumn.currentNameInGraph.snapshot();
    if (symbol && typeof symbol === 'string') {
      return {
        model: exprColumn,
        symbol,
        type: 'column' as const,
        datasetName: exprColumn.datasetName,
      };
    }
  }

  const llmColumn = context.targetedModelOfType(LlmColumnModel);
  if (llmColumn !== null) {
    const symbol = llmColumn.currentNameInGraph.snapshot();
    if (symbol && typeof symbol === 'string') {
      return {
        model: llmColumn,
        symbol,
        type: 'column' as const,
        datasetName: llmColumn.datasetName,
      };
    }
  }

  const taxonomyColumn = context.targetedModelOfType(TaxonomyColumnModel);
  if (taxonomyColumn !== null) {
    const symbol = taxonomyColumn.currentNameInGraph.snapshot();
    if (symbol && typeof symbol === 'string') {
      return {
        model: taxonomyColumn,
        symbol,
        type: 'column' as const,
        datasetName: taxonomyColumn.datasetName,
      };
    }
  }

  const toolCallColumn = context.targetedModelOfType(ToolCallModel);
  if (toolCallColumn !== null) {
    const symbol = toolCallColumn.currentNameInGraph.snapshot();
    if (symbol && typeof symbol === 'string') {
      return {
        model: toolCallColumn,
        symbol,
        type: 'column' as const,
        datasetName: toolCallColumn.datasetName,
      };
    }
  }

  // Check dataset
  const datasetModel = context.targetedModelOfType(DatasetModel);
  if (datasetModel !== null) {
    const symbol = datasetModel.currentNameInGraph.snapshot();
    if (symbol !== null && symbol !== undefined) {
      return { model: datasetModel, symbol, type: 'dataset' as const };
    }
  }

  // Check document
  const documentModel = context.targetedModelOfType(DocumentModel);
  if (documentModel !== null) {
    const symbol = documentModel.currentNameInGraph.snapshot();
    if (symbol !== null && symbol !== undefined) {
      return { model: documentModel, symbol, type: 'document' as const };
    }
  }

  // Check agent
  const agentModel = context.targetedModelOfType(AgentModel);
  if (agentModel !== null) {
    const symbol = agentModel.currentNameInGraph.snapshot();
    if (symbol !== null && symbol !== undefined) {
      return { model: agentModel, symbol, type: 'agent' as const };
    }
  }

  // Check tool
  const toolModel = context.targetedModelOfType(ToolModel);
  if (toolModel !== null) {
    const symbol = toolModel.currentNameInGraph.snapshot();
    if (symbol !== null && symbol !== undefined) {
      return { model: toolModel, symbol, type: 'tool' as const };
    }
  }

  return null;
}

function getColumnDuplicateOperations(
  graphModel: GraphModel,
  modelInfo: ModelInfo & { type: 'column' },
  newSymbol: string
): Result<DG.OperationData[], { t: string; message?: string }[]> {
  const columnInfo = graphModel.graph.snapshot().getColumn({
    rel: modelInfo.datasetName!,
    name: modelInfo.symbol,
  });

  if (columnInfo === null) {
    return err([
      {
        t: 'build',
        message: `Column not found: ${modelInfo.datasetName}.${modelInfo.symbol}`,
      },
    ]);
  }

  const duplicateOps = columnInfo.makeDuplicateOps({
    to: newSymbol,
  });

  if (duplicateOps.isErr()) {
    return err([
      {
        t: 'build',
        message: JSON.stringify(duplicateOps.error),
      },
    ]);
  }

  return ok(duplicateOps.value);
}

function getSymbolDuplicateOperations(
  graphModel: GraphModel,
  modelInfo: ModelInfo & { type: 'dataset' | 'document' | 'agent' | 'tool' },
  newSymbol: string
): Result<DG.OperationData[], { t: string; message?: string }[]> {
  const symbolInfo = graphModel.graph.snapshot().forSymbol(modelInfo.symbol);

  if (symbolInfo === null) {
    return err([
      {
        t: 'build',
        message: `Symbol not found: ${modelInfo.symbol}`,
      },
    ]);
  }

  const duplicateOps = symbolInfo.makeDuplicateOps({
    to: newSymbol,
  });

  if (duplicateOps.isErr()) {
    return err([
      {
        t: 'build',
        message: JSON.stringify(duplicateOps.error),
      },
    ]);
  }

  return ok(duplicateOps.value);
}

export class DuplicateAction extends BaseAction<
  DuplicateParams,
  { t: string; message?: string }[]
> {
  title = 'Duplicate';
  description = 'Creates a copy of this item with a new name';
  inputSchema = z.object({
    newSymbol: z.string().min(1, 'Name is required'),
  });
  group = GRAPH_ACTION_GROUP;
  icon = 'copy';

  constructor(
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    if (!context.isInScope(GraphModel)) {
      return false;
    }
    const modelInfo = getTargetedModel(context);
    return modelInfo !== null;
  }

  async execute(
    payload: DuplicateParams,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const graphModel = context.getInScopeModelOfType(GraphModel);
    const modelInfo = getTargetedModel(context);

    Assert.assert(graphModel !== null);
    Assert.assert(modelInfo !== null);

    const operationsResult =
      modelInfo.type === 'column'
        ? getColumnDuplicateOperations(
            graphModel,
            modelInfo as ModelInfo & { type: 'column' },
            payload.newSymbol
          )
        : getSymbolDuplicateOperations(
            graphModel,
            modelInfo as ModelInfo & {
              type: 'dataset' | 'document' | 'agent' | 'tool';
            },
            payload.newSymbol
          );

    if (operationsResult.isErr()) {
      return err(operationsResult.error);
    }

    const result = await graphModel.attemptToStageOperations(
      operationsResult.value
    );

    if (result.isErr()) {
      return err([
        {
          t: 'build',
          message: JSON.stringify(result.error),
        },
      ]);
    }

    return ok({});
  }

  shouldAsk(_: ApplicableContext): boolean {
    return true;
  }

  ask(
    _: Partial<DuplicateParams>,
    context: ExecuteContext
  ): AskResponse<DuplicateParams> {
    const modelInfo = getTargetedModel(context);
    Assert.assert(modelInfo !== null);

    const sourceName = modelInfo.symbol;
    const defaultName = `${sourceName} - Copy`;

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => (
            <AskView
              onRespond={async (params) => {
                await onRespond(params);
                this.modalManager.pop();
              }}
              onCancel={async () => {
                await onCancel();
                this.modalManager.pop();
              }}
              sourceName={sourceName}
              defaultName={defaultName}
              type={modelInfo.type}
            />
          ),
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}

const AskView: React.FC<{
  onRespond: (params: DuplicateParams) => Promise<void>;
  onCancel: () => void;
  sourceName: string;
  defaultName: string;
  type: 'column' | 'dataset' | 'document' | 'agent' | 'tool';
}> = ({ onRespond, onCancel, sourceName, defaultName, type }) => {
  const [newSymbol, setNewSymbol] = useState(defaultName);
  const [loading, setLoading] = useState(false);

  const typeLabel =
    type === 'column'
      ? 'Column'
      : type === 'dataset'
      ? 'Dataset'
      : type === 'document'
      ? 'Document'
      : type === 'agent'
      ? 'Agent'
      : 'Tool';

  return (
    <ModalPageView
      title={`Duplicate ${typeLabel}`}
      subtitle={`Create a copy of "${sourceName}"`}
      className="px-4 py-4"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          if (newSymbol.trim().length === 0) {
            return;
          }
          setLoading(true);
          await onRespond({ newSymbol: newSymbol.trim() });
          setLoading(false);
        }}
      >
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700">
            New {typeLabel} Name
          </label>
          <Input
            autoFocus
            placeholder="Enter name"
            value={newSymbol}
            onChange={(e) => setNewSymbol(e.target.value)}
          />
        </div>
        <div className="w-full flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            type="button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            icon="copy"
            className="w-fit self-end"
            disabled={newSymbol.trim().length === 0 || loading}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Duplicate'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
